import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  Button,
  Divider,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { useTranslation } from "react-i18next";

type Props = {
  formData: any;
  handleText: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  setArrayItem: (field: string, index: number, value: any) => void;
  addArrayItem: (field: string, value: any) => void;
  removeArrayItem: (field: string, index: number) => void;
};

export default function WarrantySection({
  formData,
  handleText,
  setArrayItem,
  addArrayItem,
  removeArrayItem,
}: Props) {
  const { t } = useTranslation("products");
  const actions = Array.isArray(formData.garantia_acoes) ? formData.garantia_acoes : [];
  const exclusions = Array.isArray(formData.garantia_exclusoes) ? formData.garantia_exclusoes : [];

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title={t("form.warrantyTitle")}
        sx={{ pb: 0.5, "& .MuiCardHeader-title": { fontSize: 16, fontWeight: 600 } }}
      />
      <CardContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("form.warrantyNoticeLabel")}
              name="garantia_alerta"
              value={formData.garantia_alerta}
              onChange={handleText}
              fullWidth
              placeholder="—"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Descrição da garantia"
              name="garantia_descricao"
              value={formData.garantia_descricao}
              onChange={handleText}
              fullWidth
              placeholder="—"
            />
          </Grid>

          {/* Ações - lista ordenada */}
          <Grid item xs={12} md={6}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <strong>{t("form.howToClaim")}</strong>
              <Button
                size="small"
                startIcon={<AddRoundedIcon />}
                onClick={() => addArrayItem("garantia_acoes", "")}
              >
                {t("form.warrantyAddStep")}
              </Button>
            </Stack>

            <Stack spacing={1.25}>
              {actions.length === 0 && (
                <Divider textAlign="center" sx={{ color: "text.secondary", mb: 1 }}>
                  {t("form.warrantyNoSteps")}
                </Divider>
              )}
              {actions.map((val: string, idx: number) => (
                <Stack key={idx} direction="row" alignItems="center" spacing={1}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={t("form.warrantyStepPh", { n: idx + 1 })}
                    value={val}
                    onChange={(e) => setArrayItem("garantia_acoes", idx, e.target.value)}
                  />
                  <Tooltip title={t("form.warrantyRemoveTooltip")}>
                    <IconButton
                      aria-label={t("form.removeAria")}
                      onClick={() => removeArrayItem("garantia_acoes", idx)}
                      size="small"
                    >
                      <DeleteRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          </Grid>

          {/* Exclusões - lista */}
          <Grid item xs={12} md={6}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <strong>{t("form.whenNotApplies")}</strong>
              <Button
                size="small"
                startIcon={<AddRoundedIcon />}
                onClick={() => addArrayItem("garantia_exclusoes", "")}
              >
                {t("form.warrantyAddExclusion")}
              </Button>
            </Stack>

            <Stack spacing={1.25}>
              {exclusions.length === 0 && (
                <Divider textAlign="center" sx={{ color: "text.secondary", mb: 1 }}>
                  {t("form.warrantyNoExclusions")}
                </Divider>
              )}
              {exclusions.map((val: string, idx: number) => (
                <Stack key={idx} direction="row" alignItems="center" spacing={1}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={t("form.warrantyExclusionPh", { n: idx + 1 })}
                    value={val}
                    onChange={(e) => setArrayItem("garantia_exclusoes", idx, e.target.value)}
                  />
                  <Tooltip title={t("form.warrantyRemoveTooltip")}>
                    <IconButton
                      aria-label={t("form.removeAria")}
                      onClick={() => removeArrayItem("garantia_exclusoes", idx)}
                      size="small"
                    >
                      <DeleteRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
